import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../lib/api";
import { Button } from "../ui/button";
import {
  WarningCircle, GoogleLogo, MicrosoftOutlookLogo, ArrowRight, X,
} from "@phosphor-icons/react";

/**
 * AbandonedCalendarBanner — surfaces a "Finish connecting your calendar"
 * prompt at the top of /dashboard for owners who started an OAuth flow
 * (gcal or outlook) but never completed it.
 *
 * Visibility rules (server-enforced via /api/me/calendar-abandoned-oauth):
 *   - User has a `google_oauth_states` OR `outlook_oauth_states` row >15min old
 *   - AND has NO completed connection for that provider yet
 *   - Frontend layer: hides for 7 days after dismissal (localStorage)
 *
 * Action: scrolls to + auto-opens the calendar section. The Dashboard
 * reads `?resume_calendar=gcal|outlook` and forwards to the right card.
 */
const DISMISS_KEY = "tt:abandoned_cal_dismissed_at";
const SNOOZE_MS = 7 * 24 * 60 * 60 * 1000;

function isSnoozed() {
  try {
    const v = localStorage.getItem(DISMISS_KEY);
    if (!v) return false;
    const ts = parseInt(v, 10);
    return Number.isFinite(ts) && Date.now() - ts < SNOOZE_MS;
  } catch {
    return false;
  }
}

export default function AbandonedCalendarBanner() {
  const navigate = useNavigate();
  const [state, setState] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    queueMicrotask(async () => {
      if (cancelled) return;
      if (isSnoozed()) {
        if (!cancelled) setDismissed(true);
        return;
      }
      try {
        const r = await api.get("/me/calendar-abandoned-oauth");
        if (!cancelled) setState(r?.data || null);
      } catch {
        if (!cancelled) setState(null);
      }
    });
    return () => { cancelled = true; };
  }, []);

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch (_ignored) { /* localStorage blocked — banner just re-renders next visit */ }
    setDismissed(true);
  };

  if (dismissed || !state) return null;
  // Show whichever provider was abandoned first; if both, prefer gcal.
  const stuck = state.gcal ? "gcal" : (state.outlook ? "outlook" : null);
  if (!stuck) return null;

  const providerLabel = stuck === "gcal" ? "Google Calendar" : "Outlook Calendar";
  const Icon = stuck === "gcal" ? GoogleLogo : MicrosoftOutlookLogo;
  const started = stuck === "gcal" ? state.gcal.started_at : state.outlook.started_at;
  const dateStr = started ? new Date(started).toLocaleDateString() : null;

  const resume = () => {
    // Hash anchor #calendar-sync scrolls to the Google/Outlook cards.
    // We keep ?resume_calendar in the URL so future enhancements can
    // auto-open a specific card / highlight a step.
    navigate(`/dashboard?resume_calendar=${stuck}#calendar-sync`);
    // Belt-and-suspenders: explicit smoothscroll in case the browser
    // skipped the hash jump (some browsers swallow it on push-state).
    setTimeout(() => {
      const el = document.getElementById("calendar-sync");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);
  };

  return (
    <div
      className="relative mb-4 rounded-2xl border border-amber-300/60 bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-950/30 dark:to-orange-950/20 dark:border-amber-700/40 p-4 sm:p-5 shadow-sm"
      data-testid="abandoned-calendar-banner"
    >
      <button
        type="button"
        onClick={dismiss}
        aria-label="Dismiss"
        className="absolute top-2 right-2 text-muted-foreground hover:text-foreground transition-colors p-1 rounded"
        data-testid="abandoned-calendar-banner-dismiss"
      >
        <X className="h-4 w-4" />
      </button>
      <div className="flex items-start gap-3 sm:gap-4">
        <div className="flex-none mt-0.5 rounded-full bg-amber-500/20 p-2">
          <WarningCircle weight="fill" className="h-5 w-5 text-amber-700 dark:text-amber-400" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-bold text-base text-amber-900 dark:text-amber-100">
              Finish connecting your {providerLabel}
            </h3>
            <Icon weight="duotone" className="h-4 w-4 text-muted-foreground" />
          </div>
          <p className="mt-1 text-sm text-amber-800 dark:text-amber-200/90 leading-relaxed">
            {dateStr ? (
              <>You started this on <strong>{dateStr}</strong> but didn&rsquo;t make it through the final permission step. </>
            ) : (
              <>You started a calendar connection but didn&rsquo;t make it through the final permission step. </>
            )}
            Pick up where you left off — most owners finish in under 90 seconds.
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <Button
              size="sm"
              onClick={resume}
              data-testid="abandoned-calendar-banner-resume"
              className="bg-amber-700 hover:bg-amber-800 text-white"
            >
              Resume setup
              <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={dismiss}
              className="text-amber-800 dark:text-amber-200/90 hover:bg-amber-100/60 dark:hover:bg-amber-900/30"
            >
              Not now
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
